import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill from 'react-quill';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../store/slices/userSlice';
import { addMessage } from '../../store/slices/chatSlice';
import { fetchTasks } from '../../store/slices/taskSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { postMessageTemplate } from '../../services/messageTemplateService';
import { ESTIMATE_MINUTES_OPTIONS, formatEstimateLabel } from '../../utils/estimateTime';
import { getWorkItemTypeOptions } from '../../utils/reactionConfig';
import { fetchOrgWorkflowReactions } from '../../store/slices/workflowCatalogSlice';
import { uploadFilesWithProgress } from '../../utils/uploadFilesWithProgress';
import { buildLegacyFileUrl } from '../../services/fileUrlService';
import { API_URL } from '../../config/api';
import 'react-quill/dist/quill.snow.css';

const APPOINTMENT_DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

const defaultAppointmentStartsAt = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const mins = d.getMinutes();
  if (mins === 0 || mins === 30) {
    d.setMinutes(mins + 30);
  } else if (mins < 30) {
    d.setMinutes(30);
  } else {
    d.setHours(d.getHours() + 1);
    d.setMinutes(0);
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const BLAST_COLORS = [
  '#F43F5E', '#FB923C', '#FBBF24', '#34D399', '#22D3EE',
  '#60A5FA', '#A78BFA', '#F472B6', '#FDE68A', '#4ADE80',
];

const stripHtml = (html) => {
  if (!html) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<img[^>]*>/gi, ' [image] ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const hasMeaningfulRichText = (html) => {
  if (!html) return false;
  if (/<img\b/i.test(html)) return true;
  const text = stripHtml(html);
  return text.length > 0;
};

const AnnouncementPreviewModal = ({ open, onClose, title, bodyHtml }) => {
  const pieces = useMemo(() => (
    Array.from({ length: 40 }, (_, i) => {
      const angle = (Math.PI * 2 * i) / 40;
      const distance = 100 + (i % 5) * 36;
      return {
        id: i,
        color: BLAST_COLORS[i % BLAST_COLORS.length],
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance - 30,
        rot: (i * 37) % 360,
        size: 6 + (i % 4) * 2,
        delay: (i % 8) * 0.03,
        shape: i % 3,
      };
    })
  ), []);

  if (!open) return null;

  const bodyText = stripHtml(bodyHtml);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/60" onClick={onClose} />
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {pieces.map((piece) => (
          <span
            key={piece.id}
            className={`announcement-blast-piece announcement-blast-shape-${piece.shape}`}
            style={{
              '--tx': `${piece.tx}px`,
              '--ty': `${piece.ty}px`,
              '--rot': `${piece.rot}deg`,
              '--size': `${piece.size}px`,
              '--delay': `${piece.delay}s`,
              '--duration': '1s',
              backgroundColor: piece.color,
              left: '50%',
              top: '42%',
            }}
          />
        ))}
      </div>
      <div className="relative w-full max-w-xl announcement-popup-card ring-2 ring-sky-400/50">
        <div className="relative rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
          <div className="h-2 w-full bg-gradient-to-r from-rose-400 via-amber-300 to-sky-400" />
          <div className="p-6 sm:p-8">
            <div className="mb-2">
              <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200">
                Announcement
              </span>
              <span className="ml-2 text-xs text-amber-600 dark:text-amber-300 font-semibold">Preview</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight mb-5">
              {title || 'Announcement'}
            </h3>
            <div className="mb-7 max-h-72 overflow-y-auto rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700">
              {bodyText ? (
                <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap leading-7 text-base sm:text-lg font-medium">
                  {bodyText}
                </p>
              ) : (
                <p className="text-gray-400 italic text-base">Fill in the fields to preview body text…</p>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-6 py-3 text-base font-bold text-white rounded-xl bg-sky-500"
                disabled
              >
                Acknowledge
              </button>
            </div>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                Close preview
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const RichFieldEditor = ({ value, onChange, placeholder }) => {
  const quillRef = useRef(null);
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: async () => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
              const token = localStorage.getItem('token');
              const results = await uploadFilesWithProgress({
                apiUrl: API_URL,
                token,
                files: [file],
              });
              const uploaded = results?.[0]?.data;
              const fileKey = uploaded?.fileUrl;
              if (!fileKey) throw new Error('Upload failed');
              const imageUrl = buildLegacyFileUrl(fileKey) || fileKey;
              const editor = quillRef.current?.getEditor();
              if (!editor) return;
              const range = editor.getSelection(true);
              const index = range ? range.index : editor.getLength();
              editor.insertEmbed(index, 'image', imageUrl, 'user');
              editor.setSelection(index + 1, 0);
            } catch (err) {
              console.error(err);
            }
          };
          input.click();
        },
      },
    },
  }), []);

  return (
    <div className="template-rich-field bg-white dark:bg-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image']}
        placeholder={placeholder || 'Write here… You can add links and images'}
        className="bg-white dark:bg-gray-900"
        style={{ minHeight: 140 }}
      />
    </div>
  );
};

const FillMessageTemplateModal = ({ isOpen, onClose, template, channelId, onPostingChange }) => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);
  const channelList = useSelector((state) => state.channels?.channels || []);
  const directMessages = useSelector((state) => state.channels?.directMessages || []);
  const workflowCatalogItems = useSelector((state) => state.workflowCatalog?.items || []);
  const workflowTypeOptions = useMemo(
    () => getWorkItemTypeOptions(workflowCatalogItems),
    [workflowCatalogItems],
  );

  const [values, setValues] = useState({});
  const [assigneeUserIds, setAssigneeUserIds] = useState([]);
  const [trackId, setTrackId] = useState('');
  const [trackLink, setTrackLink] = useState('');
  const [estimateTime, setEstimateTime] = useState('15');
  const [workflowReactionId, setWorkflowReactionId] = useState('');
  const [appointmentSubject, setAppointmentSubject] = useState('');
  const [appointmentStartsAt, setAppointmentStartsAt] = useState('');
  const [appointmentDurationMinutes, setAppointmentDurationMinutes] = useState('30');
  const [appointmentParticipantIds, setAppointmentParticipantIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const appointmentParticipantsReadyRef = useRef(false);

  const wantsTracking = (template?.postActions || []).includes('TRACKING');
  const isCustomAssignee = template?.assigneeMode === 'CUSTOM';
  const isTaskTemplate = template?.templateType === 'TASK';
  const isWorkflowTemplate = template?.templateType === 'WORKFLOW';
  const isAnnouncementTemplate = template?.templateType === 'ANNOUNCEMENT';
  const isMessageTemplate = template?.templateType === 'MESSAGE';
  const isAppointmentTemplate = template?.templateType === 'APPOINTMENT';

  const otherUsers = useMemo(
    () => (users || []).filter((u) => u.id !== currentUser?.id),
    [users, currentUser?.id]
  );

  const participantUsers = useMemo(() => {
    const all = users || [];
    const channel = [...channelList, ...directMessages].find((c) => c?.id === channelId);
    const memberIds = channel?.memberIds;
    if (Array.isArray(memberIds) && memberIds.length) {
      const byId = new Map(all.map((u) => [u.id, u]));
      return memberIds.map((id) => byId.get(id)).filter(Boolean);
    }
    return all;
  }, [users, channelList, directMessages, channelId]);

  useEffect(() => {
    if (!isOpen || !template) return;
    dispatch(fetchUsers());
    dispatch(fetchOrgWorkflowReactions());
    const initial = {};
    (template.fields || []).forEach((f) => {
      initial[f.id] = '';
    });
    setValues(initial);
    setAssigneeUserIds(template.customAssigneeUserIds || []);
    setTrackId('');
    setTrackLink('');
    setEstimateTime('15');
    setWorkflowReactionId('');
    setAppointmentSubject(template.name || '');
    setAppointmentStartsAt(defaultAppointmentStartsAt());
    setAppointmentDurationMinutes('30');
    setAppointmentParticipantIds([]);
    appointmentParticipantsReadyRef.current = false;
    setShowPreview(false);
  }, [isOpen, template, dispatch]);

  useEffect(() => {
    if (!isOpen || template?.templateType !== 'APPOINTMENT') return;
    if (appointmentParticipantsReadyRef.current) return;
    const ids = (participantUsers || []).map((u) => u.id).filter(Boolean);
    if (!ids.length && !(users || []).length) return;
    appointmentParticipantsReadyRef.current = true;
    setAppointmentParticipantIds(ids);
  }, [isOpen, template, participantUsers, users]);

  const previewBody = useMemo(() => {
    const parts = [];
    if (template?.description) parts.push(template.description);
    (template?.fields || []).forEach((field) => {
      const raw = values[field.id];
      if (!raw) return;
      if (field.fieldType === 'TEXTAREA') {
        const text = stripHtml(raw);
        if (text) parts.push(`${field.label}: ${text}`);
      } else if (String(raw).trim()) {
        parts.push(`${field.label}: ${String(raw).trim()}`);
      }
    });
    return parts.join('\n');
  }, [template, values]);

  const handleSubmit = async () => {
    if (!template?.id || !channelId) return;

    if (!isMessageTemplate) {
      for (const field of template.fields || []) {
        const raw = values[field.id] || '';
        const empty = field.fieldType === 'TEXTAREA'
          ? !hasMeaningfulRichText(raw)
          : !String(raw).trim();
        if (field.required && empty) {
          dispatch(addNotification({
            type: 'error',
            message: `"${field.label}" is required`,
          }));
          return;
        }
      }
    }
    if (wantsTracking && (!trackId.trim() || !trackLink.trim())) {
      dispatch(addNotification({
        type: 'error',
        message: 'Track ID and Track Link are required',
      }));
      return;
    }
    if (isCustomAssignee && assigneeUserIds.length === 0) {
      dispatch(addNotification({
        type: 'error',
        message: 'Select at least one assignee',
      }));
      return;
    }
    if (isTaskTemplate) {
      const estimateMinutes = estimateTime ? parseInt(estimateTime, 10) : NaN;
      if (!estimateTime || Number.isNaN(estimateMinutes) || estimateMinutes <= 0) {
        dispatch(addNotification({
          type: 'error',
          message: 'Estimate time is required for task templates',
        }));
        return;
      }
    }
    if (isWorkflowTemplate && !workflowReactionId) {
      dispatch(addNotification({
        type: 'error',
        message: 'Workflow type is required',
      }));
      return;
    }
    if (isAppointmentTemplate) {
      if (!appointmentSubject.trim()) {
        dispatch(addNotification({ type: 'error', message: 'Subject is required' }));
        return;
      }
      if (!appointmentStartsAt) {
        dispatch(addNotification({ type: 'error', message: 'Starts at is required' }));
        return;
      }
      const durationMinutes = parseInt(appointmentDurationMinutes, 10);
      if (!durationMinutes || durationMinutes <= 0) {
        dispatch(addNotification({ type: 'error', message: 'Duration is required' }));
        return;
      }
      if (!appointmentParticipantIds.length) {
        dispatch(addNotification({ type: 'error', message: 'Select at least one participant' }));
        return;
      }
    }

    const payload = {
      templateId: template.id,
      channelId,
      fieldValues: values,
      assigneeUserIds: isCustomAssignee ? assigneeUserIds : [],
      trackId: wantsTracking ? trackId.trim() : undefined,
      trackLink: wantsTracking ? trackLink.trim() : undefined,
      estimateTimeInMinutes: isTaskTemplate ? parseInt(estimateTime, 10) : undefined,
      workflowReactionId: isWorkflowTemplate ? workflowReactionId : undefined,
      appointmentSubject: isAppointmentTemplate ? appointmentSubject.trim() : undefined,
      appointmentStartsAt: isAppointmentTemplate ? appointmentStartsAt : undefined,
      appointmentDurationMinutes: isAppointmentTemplate ? parseInt(appointmentDurationMinutes, 10) : undefined,
      appointmentParticipantIds: isAppointmentTemplate ? appointmentParticipantIds : undefined,
      appointmentTimezone: isAppointmentTemplate
        ? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata')
        : undefined,
    };
    const createdLabel = isWorkflowTemplate
      ? 'Workflow created'
      : isTaskTemplate
        ? 'Task created'
        : isAnnouncementTemplate
          ? 'Announcement created'
          : isMessageTemplate
            ? 'Message sent'
            : isAppointmentTemplate
              ? 'Appointment created'
          : `${template.name || 'Template'} posted`;

    onClose();
    onPostingChange?.({
      channelId,
      label: isWorkflowTemplate
        ? 'Creating workflow…'
        : isTaskTemplate
          ? 'Creating task…'
          : isAnnouncementTemplate
            ? 'Posting announcement…'
            : isMessageTemplate
              ? 'Sending message…'
              : isAppointmentTemplate
                ? 'Creating appointment…'
            : 'Posting template…',
    });

    void (async () => {
      try {
        const message = await postMessageTemplate(payload);
        if (message) {
          dispatch(addMessage({
            channelId,
            message,
            currentUserId: currentUser?.id,
            skipUnreadCountIncrement: true,
          }));
          window.dispatchEvent(new CustomEvent('workloop:stick-timeline-to-latest', {
            detail: { channelId, messageId: message.id, force: true },
          }));
          if (
            currentUser?.id
            && message.senderId !== currentUser.id
            && message.templateInfo?.requiresAcknowledge
            && String(message.templateInfo?.templateType || '').toUpperCase() === 'ANNOUNCEMENT'
          ) {
            window.dispatchEvent(new CustomEvent('workloop:template-announcement', {
              detail: message,
            }));
          }
        }
        dispatch(addNotification({
          type: 'success',
          title: createdLabel,
          message: template.name || createdLabel,
        }));
        if (isAppointmentTemplate) {
          const startsAt = String(appointmentStartsAt || '');
          window.dispatchEvent(new CustomEvent('workloop:calendar-changed', {
            detail: {
              date: startsAt.slice(0, 10),
              startTime: startsAt.length >= 16 ? startsAt.slice(11, 16) : undefined,
            },
          }));
        }
        if (['TASK', 'ANNOUNCEMENT', 'WORKFLOW'].includes(template.templateType)) {
          dispatch(fetchTasks({}));
          window.setTimeout(() => dispatch(fetchTasks({})), 2500);
        }
      } catch (e) {
        dispatch(addNotification({
          type: 'error',
          title: 'Could not post template',
          message: e?.response?.data?.error || e?.message || 'Failed to post template',
        }));
      } finally {
        onPostingChange?.(null);
      }
    })();
  };

  if (!isOpen || !template) return null;

  const renderField = (field) => {
    if (field.fieldType === 'TEXTAREA') {
      return (
        <RichFieldEditor
          value={values[field.id] || ''}
          onChange={(html) => setValues((p) => ({ ...p, [field.id]: html }))}
          placeholder={field.placeholder || field.label}
        />
      );
    }

    const common = {
      value: values[field.id] || '',
      onChange: (e) => setValues((p) => ({ ...p, [field.id]: e.target.value })),
      className:
        'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm',
      placeholder: field.placeholder || field.label,
    };

    if (field.fieldType === 'SELECT') {
      return (
        <select {...common}>
          <option value="">Select…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (field.fieldType === 'USER') {
      return (
        <select {...common}>
          <option value="">Select user…</option>
          {(users || []).map((u) => (
            <option key={u.id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username}>
              {(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username)}
            </option>
          ))}
        </select>
      );
    }
    const inputType =
      field.fieldType === 'NUMBER' ? 'number'
        : field.fieldType === 'DATE' ? 'date'
          : 'text';
    return <input type={inputType} {...common} />;
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{template.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{template.templateType}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {template.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300">{template.description}</p>
            )}

            {isMessageTemplate && (
              <div
                className="rounded-xl bg-slate-50 dark:bg-gray-900/60 px-4 py-3 border border-slate-100 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-100 prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: template.apiBodyHtml || '<p class="text-gray-400 italic">Empty message</p>' }}
              />
            )}

            {isAppointmentTemplate && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Subject <span className="text-rose-500">*</span>
                  </label>
                  <input
                    value={appointmentSubject}
                    onChange={(e) => setAppointmentSubject(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="Appointment subject"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Starts at <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={appointmentStartsAt}
                      onChange={(e) => setAppointmentStartsAt(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Duration <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={appointmentDurationMinutes}
                      onChange={(e) => setAppointmentDurationMinutes(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    >
                      {APPOINTMENT_DURATION_OPTIONS.map((m) => (
                        <option key={m} value={String(m)}>
                          {formatEstimateLabel(m)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Participants <span className="text-rose-500">*</span>
                  </label>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                    {participantUsers.map((u) => {
                      const checked = appointmentParticipantIds.includes(u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setAppointmentParticipantIds((prev) =>
                                checked ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                              );
                            }}
                          />
                          {(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username)}
                          {u.id === currentUser?.id ? (
                            <span className="text-[10px] uppercase tracking-wide text-gray-400">you</span>
                          ) : null}
                        </label>
                      );
                    })}
                    {participantUsers.length === 0 && (
                      <p className="text-xs text-gray-500 px-1 py-2">No people available in this chat.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isWorkflowTemplate && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Workflow type <span className="text-rose-500">*</span>
                </label>
                <select
                  value={workflowReactionId}
                  onChange={(e) => setWorkflowReactionId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                >
                  <option value="">Select workflow type…</option>
                  {workflowTypeOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.emoji} {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Required for all workflow templates. Applied as the work item type on the message.
                </p>
              </div>
            )}

            {!isMessageTemplate && isAppointmentTemplate && (template.fields || []).length > 0 && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                Extra fields are added to the appointment description.
              </p>
            )}

            {!isMessageTemplate && (template.fields || []).map((field) => (
              <div key={field.id}>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {field.label}
                  {field.required ? <span className="text-rose-500"> *</span> : null}
                  {field.publicText && (isTaskTemplate || isWorkflowTemplate) ? (
                    <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                      Public text
                    </span>
                  ) : null}
                </label>
                {renderField(field)}
              </div>
            ))}

            {isCustomAssignee && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Assignees
                </label>
                <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                  {otherUsers.map((u) => {
                    const checked = assigneeUserIds.includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setAssigneeUserIds((prev) =>
                              checked ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                            );
                          }}
                        />
                        {(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username)}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {isTaskTemplate && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Estimate time <span className="text-rose-500">*</span>
                </label>
                <select
                  value={estimateTime}
                  onChange={(e) => setEstimateTime(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                >
                  {ESTIMATE_MINUTES_OPTIONS.map((m) => (
                    <option key={m} value={String(m)}>
                      {formatEstimateLabel(m)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {wantsTracking && (
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Track ID *
                  </label>
                  <input
                    value={trackId}
                    onChange={(e) => setTrackId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="e.g. PROJ-123"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Track Link *
                  </label>
                  <input
                    value={trackLink}
                    onChange={(e) => setTrackLink(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    placeholder="https://…"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              {isAnnouncementTemplate && (
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="px-4 py-2 text-sm rounded-lg border border-sky-300 text-sky-700 dark:border-sky-600 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/30"
                >
                  Preview
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {submitting
                  ? (isMessageTemplate ? 'Sending…' : isAppointmentTemplate ? 'Creating…' : 'Posting…')
                  : (isMessageTemplate ? 'Send' : isAppointmentTemplate ? 'Create appointment' : 'Post')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnnouncementPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        title={template.name}
        bodyHtml={previewBody}
      />
    </div>,
    document.body
  );
};

export default FillMessageTemplateModal;
